import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DatePicker } from '@/components/ui/date-picker';
import { PageHeader } from '@/components/efs/PageHeader';
import { TableToolbar } from '@/components/efs/TableToolbar';
import { KgCard } from '@/components/efs/KgCard';
import { MasterTable, type MasterTableColumn } from '@/components/efs/MasterTable';
import { KgValue } from '@/components/efs/KgValue';
import { cn } from '@/lib/utils';
import { formatDate, formatDateTime } from '@/lib/format';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';
import { useMaterialsList } from '@/features/masters/materials/useMaterials';

import { useInventoryMovements, useInventorySummary } from './useInventory';
import {
  movementBucket,
  type InventoryMovementLogRow,
  type InventorySummaryRow,
  type MovementsFilter,
} from './types';
import { InventoryAdjustmentDialog } from './InventoryAdjustmentDialog';

const NS = 'operations';

type TabKey = 'summary' | 'movements';

const isNegative = (kg: string): boolean => {
  const n = Number(kg);
  return Number.isFinite(n) && n < 0;
};

export const InventoryListPage = () => {
  const { t } = useTranslation(NS);
  const { batchId } = useParams();
  const currentUser = useCurrentUser();
  const canAdjust = hasPermission(currentUser, 'inventory.manage');

  // spec-13 §6.2 / round-3 item 2: a valid inventory-movement navigation
  // intent opens the movements list and visibly focuses the exact movement; a
  // materialId intent pre-filters the movements tab.
  const [searchParams] = useSearchParams();
  const focusMovementId = searchParams.get('movementId') ?? undefined;
  const focusMaterialId = searchParams.get('materialId') ?? undefined;
  const [tab, setTab] = useState<TabKey>(
    focusMovementId || focusMaterialId ? 'movements' : 'summary',
  );
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [filter, setFilter] = useState<MovementsFilter>(
    focusMaterialId ? { material_id: focusMaterialId } : {},
  );

  const summary = useInventorySummary(batchId);
  // Row click in summary applies a material filter to the movements tab.
  const movements = useInventoryMovements(batchId, filter);
  const materialsQuery = useMaterialsList({ status: 'approved', per_page: 200 });
  const materials = materialsQuery.data?.rows ?? [];

  // Stable identity so the totals below only recompute when the data changes.
  const summaryRows = useMemo(() => summary.data ?? [], [summary.data]);
  const movementRows = movements.data ?? [];

  /** Batch-wide stock position: every material's row summed per column. */
  const summaryTotals = useMemo(() => {
    const sum = (pick: (row: InventorySummaryRow) => string) =>
      summaryRows
        .reduce((acc, row) => {
          const value = Number(pick(row));
          return Number.isFinite(value) ? acc + value : acc;
        }, 0)
        .toFixed(3);
    return {
      opening_kg: sum((row) => row.opening_kg),
      in_kg: sum((row) => row.in_kg),
      out_kg: sum((row) => row.out_kg),
      current_kg: sum((row) => row.current_kg),
    };
  }, [summaryRows]);

  const summaryColumns = useMemo<MasterTableColumn<InventorySummaryRow>[]>(
    () => [
      {
        key: 'material_name',
        header: t('efs.operations.inventory.fields.material'),
        cell: (row) => (
          <button
            type="button"
            className="text-left text-efs-accent hover:underline"
            onClick={() => {
              setFilter({ material_id: row.material_id });
              setTab('movements');
            }}
          >
            {row.material_name}
          </button>
        ),
      },
      {
        key: 'opening_kg',
        header: t('efs.operations.inventory.fields.opening'),
        align: 'right',
        cell: (row) => <KgValue value={row.opening_kg} />,
      },
      {
        key: 'in_kg',
        header: t('efs.operations.inventory.fields.in'),
        align: 'right',
        cell: (row) => <KgValue value={row.in_kg} tone="success" />,
      },
      {
        key: 'out_kg',
        header: t('efs.operations.inventory.fields.out'),
        align: 'right',
        cell: (row) => <KgValue value={row.out_kg} tone="warning" />,
      },
      {
        key: 'current_kg',
        header: t('efs.operations.inventory.fields.current'),
        align: 'right',
        cell: (row) => (
          <KgValue
            value={row.current_kg}
            tone={isNegative(row.current_kg) ? 'critical' : 'default'}
          />
        ),
      },
      {
        key: 'last_movement_at',
        header: t('efs.operations.inventory.fields.last_movement_at'),
        cell: (row) => (
          <span className="text-xs text-efs-text-muted">
            {row.last_movement_at ? formatDateTime(row.last_movement_at) : '-'}
          </span>
        ),
      },
    ],
    [t],
  );

  const movementColumns = useMemo<MasterTableColumn<InventoryMovementLogRow>[]>(
    () => [
      {
        key: 'movement_date',
        header: t('efs.operations.inventory.fields.movement_date'),
        cell: (row) => formatDate(row.movement_date),
      },
      {
        key: 'material_name',
        header: t('efs.operations.inventory.fields.material'),
        cell: (row) => row.material_name,
      },
      {
        key: 'movement_type',
        header: t('efs.operations.inventory.fields.movement_type'),
        cell: (row) => {
          const bucket = movementBucket(row.movement_type);
          const label = t(`efs.operations.inventory.movement_types.${row.movement_type}`, {
            defaultValue: row.movement_type,
          });
          const toneClass =
            bucket === 'in'
              ? 'bg-emerald-50 text-emerald-900'
              : bucket === 'out'
                ? 'bg-amber-50 text-amber-900'
                : 'bg-slate-50 text-slate-700';
          return (
            <span className={cn('inline-flex rounded px-2 py-0.5 text-xs font-medium', toneClass)}>
              {label}
            </span>
          );
        },
      },
      {
        key: 'kg_delta',
        header: t('efs.operations.inventory.fields.kg_delta'),
        align: 'right',
        cell: (row) => (
          <KgValue value={row.kg_delta} tone={isNegative(row.kg_delta) ? 'warning' : 'success'} />
        ),
      },
      {
        key: 'source',
        header: t('efs.operations.inventory.fields.source'),
        cell: (row) => (
          <span className="text-xs text-efs-text-muted">{row.source_record_type ?? '-'}</span>
        ),
      },
      {
        key: 'created_by_name',
        header: t('efs.operations.inventory.fields.operator'),
        cell: (row) => row.created_by_name ?? '-',
      },
      {
        key: 'reason',
        header: t('efs.operations.inventory.fields.reason'),
        cell: (row) => <span className="text-xs text-efs-text-muted">{row.reason ?? '-'}</span>,
      },
    ],
    [t],
  );

  const negativeCount = summaryRows.filter((r) => isNegative(r.current_kg)).length;

  // Focus/scroll the exact movement row once loaded. A syntactically valid
  // but UNRESOLVED movement id must not pretend to be an exact target — the
  // page shows a localized not-found notice instead (round-3 item 2).
  const focusedMovementExists =
    !!focusMovementId && movementRows.some((r) => r.inventory_movement_id === focusMovementId);
  const focusedMovementMissing =
    !!focusMovementId && !movements.isLoading && !focusedMovementExists;
  useEffect(() => {
    if (!focusedMovementExists || !focusMovementId) return;
    const el = document.getElementById(`inventory-movement-${focusMovementId}`);
    if (el) {
      el.scrollIntoView({ block: 'center' });
      (el as HTMLElement).focus();
    }
  }, [focusedMovementExists, focusMovementId]);

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.operations.inventory.title')}
        subtitle={t('efs.operations.inventory.subtitle')}
        count={summaryRows.length}
        actions={
          batchId && canAdjust ? (
            <Button variant="outline" onClick={() => setAdjustOpen(true)}>
              <Plus className="mr-1 h-4 w-4" />
              {t('efs.operations.inventory.actions.submit_adjustment')}
            </Button>
          ) : null
        }
      />

      {/* Section selector — simple button tabs (no shadcn Tabs component). */}
      <div className="inline-flex rounded-md border border-efs-border bg-efs-bg-panel p-0.5">
        <button
          type="button"
          onClick={() => setTab('summary')}
          className={cn(
            'rounded-sm px-3 py-1.5 text-sm font-medium',
            tab === 'summary'
              ? 'bg-efs-bg-subtle text-efs-text-primary'
              : 'text-efs-text-muted hover:text-efs-text-primary',
          )}
        >
          {t('efs.operations.inventory.tabs.summary')}
        </button>
        <button
          type="button"
          onClick={() => setTab('movements')}
          className={cn(
            'rounded-sm px-3 py-1.5 text-sm font-medium',
            tab === 'movements'
              ? 'bg-efs-bg-subtle text-efs-text-primary'
              : 'text-efs-text-muted hover:text-efs-text-primary',
          )}
        >
          {t('efs.operations.inventory.tabs.movements')}
        </button>
      </div>

      {tab === 'summary' ? (
        <>
          {/* Totals across every material — the per-material rows never added
              up to a stock position for the batch as a whole. */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <KgCard
              label={t('efs.operations.inventory.fields.opening')}
              value={summaryTotals.opening_kg}
            />
            <KgCard
              label={t('efs.operations.inventory.fields.in')}
              value={summaryTotals.in_kg}
              tone="success"
            />
            <KgCard
              label={t('efs.operations.inventory.fields.out')}
              value={summaryTotals.out_kg}
              tone="warning"
            />
            <KgCard
              label={t('efs.operations.inventory.total_current', {
                defaultValue: '現在庫 合計',
              })}
              value={summaryTotals.current_kg}
              tone={isNegative(summaryTotals.current_kg) ? 'critical' : 'default'}
            />
          </div>
          {negativeCount > 0 ? (
            <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900">
              {t('efs.operations.inventory.negative_warning', { count: negativeCount })}
            </div>
          ) : null}
          <TableToolbar
            count={t('efs.operations.inventory.row_count', { count: summaryRows.length })}
          />
          <MasterTable
            columns={summaryColumns}
            rows={summaryRows}
            rowKey={(row) => row.material_id}
            isLoading={summary.isLoading}
          />
        </>
      ) : (
        <>
          <TableToolbar
            count={t('efs.operations.inventory.row_count', { count: movementRows.length })}
            filters={
              <>
                <Select
                  value={filter.material_id ?? '__all__'}
                  onValueChange={(v) =>
                    setFilter((f) => ({
                      ...f,
                      material_id: v === '__all__' ? undefined : v,
                    }))
                  }
                >
                  <SelectTrigger className="h-8 w-44">
                    <SelectValue
                      placeholder={t('efs.operations.inventory.filters.all_materials')}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">
                      {t('efs.operations.inventory.filters.all_materials')}
                    </SelectItem>
                    {materials.map((m) => (
                      <SelectItem key={m.material_id} value={m.material_id}>
                        {m.japanese_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={filter.type ?? '__all__'}
                  onValueChange={(v) =>
                    setFilter((f) => ({
                      ...f,
                      type: v === '__all__' ? undefined : (v as 'in' | 'out' | 'adjustment'),
                    }))
                  }
                >
                  <SelectTrigger className="h-8 w-32">
                    <SelectValue placeholder={t('efs.operations.inventory.filters.all_types')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">
                      {t('efs.operations.inventory.filters.all_types')}
                    </SelectItem>
                    <SelectItem value="in">{t('efs.operations.inventory.bucket.in')}</SelectItem>
                    <SelectItem value="out">{t('efs.operations.inventory.bucket.out')}</SelectItem>
                    <SelectItem value="adjustment">
                      {t('efs.operations.inventory.bucket.adjustment')}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <div className="w-36">
                  <DatePicker
                    className="h-8"
                    value={filter.from ?? ''}
                    onChange={(value) => setFilter((f) => ({ ...f, from: value || undefined }))}
                  />
                </div>
                <div className="w-36">
                  <DatePicker
                    className="h-8"
                    value={filter.to ?? ''}
                    onChange={(value) => setFilter((f) => ({ ...f, to: value || undefined }))}
                  />
                </div>
                {Object.values(filter).some((v) => v) ? (
                  <Button type="button" variant="ghost" size="sm" onClick={() => setFilter({})}>
                    {t('efs.operations.inventory.filters.clear')}
                  </Button>
                ) : null}
              </>
            }
          />
          {focusedMovementMissing ? (
            <div
              role="status"
              className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800"
            >
              {t('efs.operations.inventory.movement_not_found')}
            </div>
          ) : null}
          <MasterTable
            columns={movementColumns}
            rows={movementRows}
            rowKey={(row) => row.inventory_movement_id}
            rowProps={(row) =>
              row.inventory_movement_id === focusMovementId
                ? {
                    id: `inventory-movement-${row.inventory_movement_id}`,
                    tabIndex: -1,
                    // Theme-aware highlight: readable text in light AND dark mode.
                    className:
                      'bg-amber-100 ring-2 ring-inset ring-amber-400 dark:bg-amber-900/40',
                  }
                : undefined
            }
            isLoading={movements.isLoading}
          />
        </>
      )}

      {batchId ? (
        <InventoryAdjustmentDialog
          batchId={batchId}
          open={adjustOpen}
          onOpenChange={setAdjustOpen}
        />
      ) : null}
    </div>
  );
};

export default InventoryListPage;
